import { getCurrentInstance } from '@vue/composition-api';
import { hasExplicitModuleAccess } from '@/libs/acl/routeProtection';
import { isActiveAdminDkp } from '@/libs/acl/activeAdminDkp';

/**
 * Returns ability result if ACL is configured or else just return true
 * Useful if you don't know if ACL is configured or not
 * Used in @core files to handle absence of ACL without errors
 * @param {String} action CASL Actions // https://casl.js.org/v4/en/guide/intro#basics
 * @param {String} subject CASL Subject // https://casl.js.org/v4/en/guide/intro#basics
 */
export const can = (action, subject) => {
  const vm = getCurrentInstance().proxy;
  return vm.$can ? vm.$can(action, subject) : true;
};

/**
 * Tenant-level preference gate. When an item has `tenantPref: 'someKey'`,
 * it will only be visible while `preferencias/someKey` is true in the store.
 * If the store/getter is not present yet, defaults to true (don't hide).
 */
export const passesTenantPref = (item) => {
  if (!item || !item.tenantPref) return true;
  try {
    const vm = getCurrentInstance().proxy;
    const store = vm && vm.$store;
    if (!store || !store.getters) return true;
    const key = `preferencias/${item.tenantPref}`;
    if (!(key in store.getters)) return true;
    return store.getters[key] === true;
  } catch (e) {
    return true;
  }
};

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
/**
 * DKP-only gate. When an item has `dkpOnly: true`, it is visible only while the
 * current tenant slug is 'dkp'. Other tenants must never see it (e.g. the
 * Tenants admin module — clicking it would switch the session to the DKP tenant
 * and break the current tenant). If the slug can't be determined, the item is
 * hidden (safe default).
 */
export const passesDkpOnly = (item) => {
  if (!item || !item.dkpOnly) return true;
  try {
    const vm = getCurrentInstance().proxy;
    const store = vm && vm.$store;
    let slug = null;
    if (store && store.getters && 'tenant/tenantSlug' in store.getters) {
      slug = store.getters['tenant/tenantSlug'];
    }
    if (!slug && typeof localStorage !== 'undefined') {
      slug = localStorage.getItem('tenantSlug');
    }
    return String(slug || '').trim().toLowerCase() === 'dkp';
  } catch (e) {
    return false;
  }
};

export const canViewVerticalNavMenuLink = (item) => {
  if (item.activeAdminDkpOnly && !isActiveAdminDkp()) return false;
  if (!passesDkpOnly(item) || !passesTenantPref(item)) return false;
  if (item.explicitModule) {
    try {
      const vm = getCurrentInstance().proxy;
      return hasExplicitModuleAccess(item.action, item.resource, vm.$ability && vm.$ability.rules);
    } catch (e) {
      return false;
    }
  }
  return can(item.action, item.resource);
};

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style
export const canViewVerticalNavMenuGroup = (item) => {
  // ! This same logic is used in canViewHorizontalNavMenuGroup and canViewHorizontalNavMenuHeaderGroup. So make sure to update logic in them as well
  if (!passesDkpOnly(item) || !passesTenantPref(item)) return false;
  const hasAnyVisibleChild = item.children.some((i) => passesDkpOnly(i) && passesTenantPref(i) && can(i.action, i.resource));

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }
  return can(item.action, item.resource) && hasAnyVisibleChild;
};

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewVerticalNavMenuHeader = (item) => passesDkpOnly(item) && passesTenantPref(item) && can(item.action, item.resource);

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewHorizontalNavMenuLink = (item) => (item.activeAdminDkpOnly
  ? canViewVerticalNavMenuLink(item) : can(item.action, item.resource));

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource
 * @param {Object} item navigation object item
 */
export const canViewHorizontalNavMenuHeaderLink = (item) => can(item.action, item.resource);

/**
 * Check if user can view item based on it's ability
 * Based on item's action and resource & Hide group if all of it's children are hidden
 * @param {Object} item navigation object item
 */
// eslint-disable-next-line arrow-body-style
export const canViewHorizontalNavMenuGroup = (item) => {
  // ? Same logic as canViewVerticalNavMenuGroup
  const hasAnyVisibleChild = item.children.some((i) => can(i.action, i.resource));

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }
  return can(item.action, item.resource) && hasAnyVisibleChild;
};

// eslint-disable-next-line arrow-body-style
export const canViewHorizontalNavMenuHeaderGroup = (item) => {
  // ? Same logic as canViewVerticalNavMenuGroup but with extra content

  // eslint-disable-next-line arrow-body-style
  const hasAnyVisibleChild = item.children.some((grpOrItem) => {
    // If it have children => It's grp
    // Call ACL function based on grp/link
    return grpOrItem.children ? canViewHorizontalNavMenuGroup(grpOrItem) : canViewHorizontalNavMenuLink(grpOrItem);
  });

  // If resource and action is defined in item => Return based on children visibility (Hide group if no child is visible)
  // Else check for ability using provided resource and action along with checking if has any visible child
  if (!(item.action && item.resource)) {
    return hasAnyVisibleChild;
  }
  return can(item.action, item.resource) && hasAnyVisibleChild;
};
